import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { storeActivityIndicator } from '../../../../../redux/reducers/authSlice';
import { fiyatYaz, sayiYaz } from '../../../../../helpers/yakitHesap';
import { excelAktarimMesaji } from '../../../../../helpers/yakitAktarim';
import { YakitExcelSonucu, YakitService } from '../../../../../services/YakitService';

interface Props {
    show: boolean
    onClose: () => void
    onAktarildi: () => void
    ornekExcel: () => void
}

/** Hücreler: metin kelimeden kırılır (yatay kaydırma olmasın); sayılar sağa dayalı, tek satır. */
const HUCRE: React.CSSProperties = { paddingLeft: '0.5rem', paddingRight: '0.5rem', whiteSpace: 'normal', wordBreak: 'break-word' };
const SAYI: React.CSSProperties = { paddingLeft: '0.5rem', paddingRight: '0.75rem', textAlign: 'right', whiteSpace: 'nowrap' };
/** Başlık: liste ekranlarındaki standart renk (#8DC1FF, siyah yazı). */
const BASLIK: React.CSSProperties = { ...HUCRE, background: '#8DC1FF', color: '#000', verticalAlign: 'bottom', lineHeight: 1.3 };

/**
 * Yakıt Kayıtları · Excel'den içe aktarma (27.10.2026, #490): HGS penceresiyle aynı kalıp. Dosya(lar) seç → önizle
 * (hiçbir şey kaydedilmez) → Aktar. Her satır bir yakıt satırı; aynı belge no + tarih + plaka tek fiş.
 * #547 (04.11.2026): Hatalı satır Aktar'ı kapatmaz (yalnız dosya geneli hata kapatır): sağlam satırlar aktarılır, hatalı
 * satırlar Aktarım Hataları ekranına düşer, zaten kayıtlı satırlar (tekrar) atlanır ve gri "Zaten kayıtlı" görünür.
 * Uyarılar (aynı belge no, sefer başka araca ait) aktarmayı engellemez.
 */
const YakitIceAktarModal = ({ show, onClose, onAktarildi, ornekExcel }: Props) => {
    const dispatch = useDispatch();
    const [dosyalar, setDosyalar] = useState<File[]>([]);
    const [sonuc, setSonuc] = useState<YakitExcelSonucu | undefined>(undefined);
    const [yalnizSorunlular, setYalnizSorunlular] = useState<boolean>(false);
    const girdi = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setDosyalar([]);
        setSonuc(undefined);
        setYalnizSorunlular(false);
        if (girdi.current)
            girdi.current.value = '';
    }, [show])

    const onizle = async (secilen: File[] = dosyalar) => {
        if (secilen.length == 0) {
            toast.error('Önce Excel dosyasını seçin.');
            return;
        }
        try {
            dispatch(storeActivityIndicator(true));
            var response = await YakitService.excelOnizle(secilen);
            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }
            const s = response.data as YakitExcelSonucu;
            setSonuc(s);
            setYalnizSorunlular(s.hataliSatir > 0);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const aktar = async () => {
        if (dosyalar.length == 0 || !sonuc)
            return;
        try {
            dispatch(storeActivityIndicator(true));
            var response = await YakitService.excelAktar(dosyalar);
            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }
            const s = response.data as YakitExcelSonucu;
            if (s.hata) {
                setSonuc(s);
                toast.error(s.hata);
            }
            else if (s.aktarilan > 0 || (s.hataEkraninda ?? 0) > 0) {
                const mesaj = excelAktarimMesaji(s.aktarilan, s.hataEkraninda);
                if (s.aktarilan > 0)
                    toast.success(mesaj, { duration: 6000 });
                else
                    toast.error(mesaj, { duration: 6000 });
                onAktarildi();
            }
            else {
                setSonuc(s);
                toast.error((s.tekrarSatir ?? 0) > 0 ? 'Bütün satırlar zaten kayıtlı; aktarılacak fiş yok.' : 'Aktarılacak fiş yok.');
            }
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    // Hatalı satır Aktar'ı kapatmaz (#547); yalnız dosya geneli hata ya da boş dosya kapatır.
    const aktarilabilir = !!sonuc && !sonuc.hata && sonuc.satirSayisi > 0;
    const tekrarSatir = sonuc?.tekrarSatir ?? 0;
    const satirlar = (sonuc?.satirlar ?? []).filter(x => !yalnizSorunlular || x.tekrar || x.hatalar.length > 0 || x.uyarilar.length > 0);
    const cokDosya = (sonuc?.dosyalar ?? []).length > 1;

    return (
        <Modal show={show} onHide={onClose} size="xl" centered scrollable dialogClassName="hgs-ice-aktar">
            <Modal.Header closeButton>
                <Modal.Title>Excel'den içe aktar · Yakıt Kayıtları</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
                    <input
                        id="yakit-excel-dosya"
                        ref={girdi}
                        type="file"
                        accept=".xlsx,.xls"
                        multiple
                        className="form-control form-control-sm w-auto"
                        onChange={e => { const f = Array.from(e.target.files ?? []); setDosyalar(f); setSonuc(undefined); if (f.length > 0) onizle(f); }}
                    />
                    <button className="btn btn-sm btn-light-primary" disabled={dosyalar.length == 0} onClick={() => onizle()}>Yeniden önizle</button>
                    <button className="btn btn-sm btn-light-success" onClick={ornekExcel}><i className="fa fa-file-excel me-1" />Örnek Excel indir</button>
                </div>

                <div className="text-muted fs-7 mb-4">
                    Örnek Excel'i doldurun: 2. satır örnektir, kayıtlarınızı altına girip örnek satırı silin (silmezseniz atlanır).
                    Her satır bir yakıt satırıdır; aynı Belge No + İşlem Tarihi + Araç Plaka tek fişte birleşir. Birim Fiyat yazılmazsa Brüt Toplam'dan geriye hesaplanır.
                    Önizlemede hiçbir şey kaydedilmez. Aktar'da sağlam satırlar aktarılır, hatalı satırlar Aktarım Hataları ekranına düşer (orada düzeltilir), zaten kayıtlı satırlar atlanır.
                    Petrol Ofisi "Yakıt Alım Detay Raporu" da doğrudan yüklenebilir: her satış ayrı fiş olur, tutar KDV dahil (%20) sayılır, sürücü araç kartından alınır. Diğer firmaların dökümleri örnek dosyaları gelince eklenecek.
                </div>

                {sonuc?.hata && <div className="alert alert-danger py-3">{sonuc.hata}</div>}

                {
                    sonuc && !sonuc.hata && (
                        <React.Fragment>
                            <div className="d-flex flex-wrap gap-6 mb-3 fs-6">
                                <span><b>{sonuc.satirSayisi}</b> satır · <b>{sonuc.fisSayisi}</b> fiş</span>
                                <span className={sonuc.hataliSatir > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>{sonuc.hataliSatir} hatalı</span>
                                {sonuc.uyariliSatir > 0 && <span className="text-warning fw-bold">{sonuc.uyariliSatir} uyarılı</span>}
                                {tekrarSatir > 0 && <span className="text-gray-600 fw-bold">{tekrarSatir} zaten kayıtlı</span>}
                                <span>Brüt toplam: <b>{sayiYaz(sonuc.toplamBrutTL)} TL</b></span>
                                {sonuc.ornekSatirAtlandi && <span className="text-muted">Örnek satır atlandı</span>}
                            </div>

                            {sonuc.ayniDosyaUyarisi && <div className="alert alert-warning py-2">{sonuc.ayniDosyaUyarisi}</div>}

                            {
                                sonuc.hataliSatir > 0 && (
                                    <div id="yakit-hatali-ozet" className="alert alert-light-danger py-2 mb-2">
                                        {sonuc.hataliSatir} satır hatalı — Aktar'da bu satırlar Aktarım Hataları ekranına düşer, sağlam satırlar aktarılır.
                                    </div>
                                )
                            }

                            {
                                tekrarSatir > 0 && (
                                    <div id="yakit-tekrar-ozet" className="alert alert-secondary py-2 mb-2">
                                        {tekrarSatir} satır zaten kayıtlı (aktarılmaz).
                                    </div>
                                )
                            }

                            {
                                (sonuc.hataliSatir > 0 || sonuc.uyariliSatir > 0 || tekrarSatir > 0) && (
                                    <label className="form-check form-check-sm form-check-custom form-check-solid mb-3">
                                        <input id="yakit-yalniz-sorunlular" className="form-check-input" type="checkbox" checked={yalnizSorunlular} onChange={() => setYalnizSorunlular(!yalnizSorunlular)} />
                                        <span className="form-check-label">Yalnız hatalı, uyarılı ve zaten kayıtlı satırları göster</span>
                                    </label>
                                )
                            }

                            <table className="table table-row-dashed ebbe-compact-table fs-7 align-middle otofatura-onizleme-tablo" style={{ tableLayout: 'fixed', width: '100%' }}>
                                <colgroup>
                                    {cokDosya && <col style={{ width: '7%' }} />}
                                    <col style={{ width: '4%' }} />
                                    <col style={{ width: '3%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: '4%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: cokDosya ? '14%' : '21%' }} />
                                </colgroup>
                                <thead>
                                    <tr className="fw-bolder text-uppercase">
                                        {cokDosya && <th style={BASLIK}>Dosya</th>}
                                        <th style={BASLIK}>Satır</th>
                                        <th style={BASLIK}>Fiş</th>
                                        <th style={BASLIK}>Belge No</th>
                                        <th style={BASLIK}>Tarih</th>
                                        <th style={BASLIK}>Plaka</th>
                                        <th style={BASLIK}>Sürücü</th>
                                        <th style={BASLIK}>Yakıt Türü</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>Miktar</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>Birim Fiyat</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>KDV %</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>Toplam</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>KDV</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>Brüt</th>
                                        <th style={BASLIK}>Durum</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-700 fw-semibold">
                                    {satirlar.length == 0 && <tr><td colSpan={16} className="text-center text-muted">Gösterilecek satır yok.</td></tr>}
                                    {
                                        satirlar.map(s => (
                                            <tr key={(s.dosya ?? '') + '-' + s.satirNo} className={s.tekrar ? 'bg-light text-muted' : s.hatalar.length > 0 ? 'bg-light-danger' : undefined}>
                                                {cokDosya && <td style={HUCRE}>{s.dosya}</td>}
                                                <td style={HUCRE}>{s.satirNo}</td>
                                                <td style={HUCRE}>{s.fisNo}</td>
                                                <td style={HUCRE}>{s.belgeNo}</td>
                                                <td style={HUCRE}>{s.islemTarihi}</td>
                                                <td style={HUCRE}>{s.plaka}</td>
                                                <td style={HUCRE}>{s.surucu}</td>
                                                <td style={HUCRE}>{s.yakitTuru}</td>
                                                <td className="sayi" style={SAYI}>{sayiYaz(s.miktar)}{s.birim ? ' ' + s.birim : ''}</td>
                                                <td className="sayi" style={SAYI}>{fiyatYaz(s.birimFiyat)}</td>
                                                <td className="sayi" style={SAYI}>{s.kdvOrani != null ? '%' + s.kdvOrani : ''}</td>
                                                <td className="sayi" style={SAYI}>{sayiYaz(s.net)}</td>
                                                <td className="sayi" style={SAYI}>{sayiYaz(s.kdv)}</td>
                                                <td className="sayi" style={SAYI}>{sayiYaz(s.brut)}{s.dovizCinsi && s.dovizCinsi != 'TRY' && s.dovizCinsi != 'TL' ? ' ' + s.dovizCinsi : ''}</td>
                                                <td style={HUCRE}>
                                                    {
                                                        s.tekrar
                                                            ? <span className="badge badge-light text-gray-600" title="Bu fiş daha önce aktarılmış; atlanır">Zaten kayıtlı</span>
                                                            : (
                                                                <React.Fragment>
                                                                    {s.hatalar.length > 0 && <ul className="text-danger mb-0 ps-4">{s.hatalar.map((h, i) => <li key={i}>{h}</li>)}</ul>}
                                                                    {s.uyarilar.length > 0 && <ul className="text-warning mb-0 ps-4">{s.uyarilar.map((h, i) => <li key={i}>{h}</li>)}</ul>}
                                                                    {s.hatalar.length == 0 && s.uyarilar.length == 0 && <span className="badge badge-light-success">Uygun</span>}
                                                                </React.Fragment>
                                                            )
                                                    }
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </React.Fragment>
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-sm btn-light" onClick={onClose}>Kapat</button>
                <button className="btn btn-sm btn-primary" disabled={!aktarilabilir} onClick={aktar}>
                    {sonuc && sonuc.fisSayisi > 0 ? `${sonuc.fisSayisi} fişi aktar` : 'Aktar'}
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default YakitIceAktarModal;
